import { ModalContext } from 'App';
import Modal from 'modules/common/components/Modal';
import React, { useContext, useEffect } from 'react';
import _ from 'lodash';
import OtpInput from 'react-otp-input';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import * as actions from '../actions';
import * as c from '../constants';

const OTP_DURATION = 120; // 2 minutes
const OTP_LOCKED_MESSAGE = 'Sending OTP is temporarily locked.';

const formatTime = (total) => {
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const OtpModal = ({
  verifyOtp,
  verifyOtpSource,
  resendOtp,
  isLoading,
  isLoadingVerifyOtpSource,
  isLoadingResend,
  otpError,
  history,
  pathName,
}) => {
  const setModal = useContext(ModalContext);
  const [otpPayload, setOtpPayload] = React.useState({
    pin: '',
    otpReceiver: '',
    otpSource: '',
  });
  const [secondsLeft, setSecondsLeft] = React.useState(OTP_DURATION);
  const [isOtpLocked, setIsOtpLocked] = React.useState(false);
  const otpErrorDetail = _.get(otpError, 'response.errors.0.detail');

  useEffect(() => {
    const otpReceiver = localStorage.getItem('otpReceiver');
    const otpSource = localStorage.getItem('otpSource');
    setOtpPayload((prev) => ({
      ...prev,
      otpReceiver,
      otpSource,
    }));

    return () => {
      setOtpPayload({});
      localStorage.removeItem('email');
      localStorage.removeItem('otpReceiver');
      localStorage.removeItem('otpSource');
      localStorage.removeItem('auth_token');
    };
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const id = setTimeout(() => {
      setSecondsLeft((prev) => prev - 1);
    }, 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  useEffect(() => {
    if (otpErrorDetail !== OTP_LOCKED_MESSAGE) return;
    setIsOtpLocked(true);
    setSecondsLeft((prev) => (prev > 0 ? prev : OTP_DURATION));
  }, [otpError, otpErrorDetail]);

  const handleChange = (pin) => {
    if (isOtpLocked) return;
    setOtpPayload((prev) => ({ ...prev, pin }));
  };

  const closeModal = () => setModal({ isOpen: false });

  const handleResend = () => {
    const authToken = localStorage.getItem('auth_token');
    if (!authToken) return;
    resendOtp({ auth_token: authToken }, () => {
      setIsOtpLocked(false);
      setSecondsLeft(OTP_DURATION);
      setOtpPayload((prev) => ({ ...prev, pin: '' }));
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isOtpLocked) return;

    if (otpPayload.otpSource) {
      verifyOtpSource(
        {
          otp_source: otpPayload.otpSource,
          pin: otpPayload.pin,
        },
        () => {
          setOtpPayload({});
          closeModal();
        }
      );
      return;
    }

    const authToken = localStorage.getItem('auth_token');
    verifyOtp(
      {
        auth_token: authToken,
        pin: otpPayload.pin,
      },
      (profile) => {
        setOtpPayload({});
        closeModal();
        const redirectPage = profile.redirect_page;
        history.push(redirectPage ? _.get(profile, 'redirect_page') : pathName);
      }
    );
  };

  return (
    <Modal.Body>
      <form
        onSubmit={handleSubmit}
        className="d-flex flex-column align-items-center p-3"
        style={{ gap: '.5em' }}
      >
        <h2 className="font-weight-bold">OTP VERIFICATION</h2>
        <p className="text-center">
          Enter the One-Time-PIN verification code you received in{' '}
          <span className="text-primary font-weight-bold">
            {otpPayload.otpReceiver}
          </span>
          .
        </p>

        <OtpInput
          shouldAutoFocus={!isOtpLocked}
          value={otpPayload.pin}
          onChange={handleChange}
          numInputs={6}
          isInputDisabled={isOtpLocked}
          //   renderSeparator={<span>-</span>}
          renderInput={(props) => (
            <input {...props} disabled={isOtpLocked} />
          )}
          containerStyle={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
          }}
          inputStyle={{
            width: '3.5rem',
            height: '3.5rem',
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            fontSize: '1.5rem',
            fontWeight: 'bold',
          }}
        />

        <p className="mt-2 mb-0 font-weight-bold text-dark">
          {formatTime(secondsLeft)}
        </p>

        <button
          className="btn btn-dark btn-outline-dark btn-block mt-3 text-white p-3"
          type="submit"
          disabled={isLoading || isLoadingVerifyOtpSource || isOtpLocked}
        >
          Verify OTP
        </button>

        <button
          type="button"
          className="btn btn-dark btn-outline-dark btn-block text-white p-3"
          onClick={handleResend}
          disabled={secondsLeft > 0 || isLoadingResend}
        >
          Resend OTP
        </button>

        <button type="button" className="btn btn-link" onClick={closeModal}>
          Cancel
        </button>
      </form>
    </Modal.Body>
  );
};

OtpModal.propTypes = {
  verifyOtp: PropTypes.instanceOf(Function).isRequired,
  verifyOtpSource: PropTypes.instanceOf(Function).isRequired,
  resendOtp: PropTypes.instanceOf(Function).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingVerifyOtpSource: PropTypes.bool.isRequired,
  isLoadingResend: PropTypes.bool.isRequired,
  otpError: PropTypes.instanceOf(Object),
  pathName: PropTypes.string.isRequired,
};

OtpModal.defaultProps = {
  otpError: null,
};

const mapStateToProps = ({ api }, { location }) => ({
  isLoading: api.loading.indexOf(c.VERIFY_OTP) > -1,
  isLoadingVerifyOtpSource: api.loading.indexOf(c.VERIFY_OTP_SOURCE) > -1,
  isLoadingResend: api.loading.indexOf(c.RESEND_OTP) > -1,
  otpError:
    _.get(api, ['errors', c.VERIFY_OTP]) ||
    _.get(api, ['errors', c.VERIFY_OTP_SOURCE]),
  pathName: _.get(location, 'pathname'),
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(OtpModal);
